// Breeze — severity kanban (iter34 replacement for the horizontal strip).
//
// 5 fixed columns: S1 · S2 · S3 · S4 · Precaution.
// Cards within each column sorted by opened_at ascending (oldest first).
// Read-only — click opens the existing side panel via openPanel(id).
// Mobile (<768px): only S1 + S2 are shown by default; "Show all →" toggles
// the rest.
import React, { useMemo, useState } from "react";
import { useStore } from "../store";
import { severityColor } from "../data/mockData";
import { useT } from "../lib/i18n";

const SEVERITIES = ["s1", "s2", "s3", "s4", "precaution"];
const SEV_BADGE = { s1: "S1", s2: "S2", s3: "S3", s4: "S4", precaution: "PRE" };
const MAX_CARD_TITLE = 70;

const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || "");

const ageLabel = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const statusBadge = (status) => {
  const map = {
    OPEN: { bg: "rgba(24,95,165,0.18)", color: "#9ec3ec" },
    open: { bg: "rgba(24,95,165,0.18)", color: "#9ec3ec" },
    ESCALATED: { bg: "rgba(226,75,74,0.18)", color: "#ff9a99" },
    escalated: { bg: "rgba(226,75,74,0.18)", color: "#ff9a99" },
    IN_PROGRESS: { bg: "rgba(243,177,74,0.18)", color: "#f3b14a" },
    "IN PROGRESS": { bg: "rgba(243,177,74,0.18)", color: "#f3b14a" },
    in_progress: { bg: "rgba(243,177,74,0.18)", color: "#f3b14a" },
    contained: { bg: "rgba(78,208,132,0.18)", color: "#4ed084" },
    resolved: { bg: "rgba(78,208,132,0.18)", color: "#4ed084" },
    closed: { bg: "rgba(120,125,140,0.18)", color: "#9aa1b3" },
  };
  const key = (status || "OPEN").toString();
  const cfg = map[key] || map[key.toLowerCase()] || map.OPEN;
  return { ...cfg, key: key.toLowerCase() };
};

const slaBarColor = (pct) => {
  if (pct >= 100) return "#E24B4A";
  if (pct >= 75) return "#f3b14a";
  return "#4ed084";
};

export const BreezeStrip = ({ incidents = [], summary }) => {
  const [mobileShowAll, setMobileShowAll] = useState(false);
  const openPanel = useStore((s) => s.openPanel);
  const { t } = useT();

  const byCol = useMemo(() => {
    const out = {};
    for (const s of SEVERITIES) out[s] = [];
    for (const inc of incidents) {
      const sev = (inc.severity || "").toLowerCase();
      const key = SEVERITIES.includes(sev) ? sev : (sev === "p" || sev === "pre" ? "precaution" : null);
      if (!key) continue;
      out[key].push(inc);
    }
    // Sort oldest-first per column.
    for (const s of SEVERITIES) {
      out[s].sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime();
        const tb = new Date(b.createdAt || 0).getTime();
        return ta - tb;
      });
    }
    return out;
  }, [incidents]);

  return (
    <section
      className="mt-4 rounded-md p-4"
      style={{
        background: "#111420",
        border: "1px solid rgba(155,89,182,0.30)",
      }}
      data-testid="breeze-section"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded"
            style={{
              background: "rgba(155,89,182,0.20)",
              color: "#c89adb",
              fontFamily: "'DM Mono', monospace", fontSize: 8,
              letterSpacing: "1.2px", fontWeight: 700,
            }}
          >
            SITREP
          </span>
          <span
            style={{
              fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14,
              color: "#f0f2f8", letterSpacing: "0.3px",
            }}
          >
            {t("breeze.title")}
          </span>
        </div>
        <div
          style={{
            fontFamily: "'DM Mono', monospace", fontSize: 10,
            color: "#9198b0", letterSpacing: "0.4px",
          }}
          data-testid="breeze-summary"
        >
          {summary}
        </div>
      </div>

      {/* Board */}
      <div
        className="grid grid-cols-2 md:grid-cols-5 gap-3"
        style={{ maxHeight: 420 }}
        data-testid="breeze-board"
      >
        {SEVERITIES.map((sev, idx) => {
          const items = byCol[sev];
          const breaching = items.filter((i) => (i.sla || 0) > 100).length;
          const hiddenOnMobile = idx >= 2 && !mobileShowAll;
          return (
            <div
              key={sev}
              className={`flex flex-col rounded-md ${hiddenOnMobile ? "hidden md:flex" : ""}`}
              style={{
                background: "#0a0c12",
                border: "1px solid rgba(255,255,255,0.06)",
                maxHeight: 420,
              }}
              data-testid={`breeze-column-${sev}`}
            >
              <BreezeColumnHeader sev={sev} count={items.length} breaching={breaching} />
              <div
                className="flex-1 overflow-y-auto px-2 py-2 space-y-2"
                style={{ scrollbarWidth: "thin" }}
                data-testid={`breeze-column-body-${sev}`}
              >
                {items.length === 0 && (
                  <div
                    className="text-center py-4"
                    style={{
                      fontFamily: "'DM Mono', monospace", fontSize: 10,
                      color: "#6b7388", letterSpacing: "0.4px",
                    }}
                    data-testid={`breeze-column-empty-${sev}`}
                  >
                    {t("breeze.no_incidents_in", { sev: t(`severity.${sev}`) })}
                  </div>
                )}
                {items.map((inc) => (
                  <BreezeCard
                    key={inc.id}
                    inc={inc}
                    onClick={() => openPanel(inc.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile toggle */}
      <button
        onClick={() => setMobileShowAll((v) => !v)}
        className="md:hidden mt-2 w-full text-center py-2 rounded"
        style={{
          background: "transparent",
          border: "1px dashed rgba(155,89,182,0.40)",
          color: "#c89adb",
          fontFamily: "'DM Mono', monospace", fontSize: 11,
          letterSpacing: "0.4px", cursor: "pointer",
        }}
        data-testid="breeze-mobile-toggle"
      >
        {mobileShowAll ? t("breeze.show_fewer") : t("breeze.show_all")}
      </button>
    </section>
  );
};

const BreezeColumnHeader = ({ sev, count, breaching }) => {
  const c = severityColor(sev);
  const { t } = useT();
  return (
    <div
      className="flex items-center gap-2 px-3 py-2"
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
      data-testid={`breeze-column-header-${sev}`}
    >
      <span
        style={{
          background: `${c}22`, color: c,
          fontFamily: "'DM Mono', monospace", fontSize: 10,
          padding: "2px 6px", borderRadius: 3,
          fontWeight: 700, letterSpacing: "0.5px",
        }}
      >
        {SEV_BADGE[sev]}
      </span>
      <span
        style={{
          fontFamily: "'DM Mono', monospace", fontSize: 11,
          color: "#f0f2f8", fontWeight: 600,
        }}
        data-testid={`breeze-column-count-${sev}`}
      >
        {count}
      </span>
      {breaching > 0 && (
        <span
          className="ml-auto"
          style={{
            fontFamily: "'DM Mono', monospace", fontSize: 9,
            color: "#ff9a99", letterSpacing: "0.4px",
          }}
          data-testid={`breeze-column-breaching-${sev}`}
        >
          {t("incidents.kanban_breaching", { n: breaching })}
        </span>
      )}
    </div>
  );
};

const BreezeCard = ({ inc, onClick }) => {
  const sev = severityColor(inc.severity);
  const sla = Number.isFinite(inc.sla) ? inc.sla : 0;
  const breached = sla > 100;
  const watchSoon = sla >= 80 && !breached && inc.severity !== "s1";
  const bar = Math.min(100, Math.max(0, sla));
  const badge = statusBadge(inc.status);
  const { t } = useT();
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded transition-colors"
      style={{
        background: "#0d0f18",
        border: `1px solid ${breached ? "#E24B4A" : watchSoon ? "transparent" : "rgba(255,255,255,0.08)"}`,
        borderLeft: `3px solid ${sev}`,
        borderRadius: 4,
        padding: "8px 10px",
        cursor: "pointer",
        position: "relative",
        animation: breached ? "breeze-card-pulse-red 1.6s ease-in-out infinite" : undefined,
        outline: watchSoon ? "1px dashed #f3b14a" : "none",
        outlineOffset: watchSoon ? -2 : 0,
        minHeight: 90,
      }}
      data-testid={`breeze-card-${inc.id}`}
      data-severity={inc.severity}
      data-sla-breach={breached ? "true" : "false"}
      data-watch={watchSoon ? "true" : "false"}
    >
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block rounded-full"
          style={{ width: 6, height: 6, background: sev }}
        />
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9ec3ec" }}>
          {inc.id}
        </span>
        {watchSoon && (
          <span
            className="ml-auto"
            style={{
              background: "rgba(243,177,74,0.18)", color: "#f3b14a",
              fontFamily: "'DM Mono', monospace", fontSize: 8,
              padding: "1px 4px", borderRadius: 2, letterSpacing: "0.4px",
            }}
            data-testid="breeze-card-watch-badge"
          >
            {t("incidents.kanban_watch")}
          </span>
        )}
      </div>
      <div
        className="mt-1"
        style={{
          fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 11,
          color: "#f0f2f8", lineHeight: 1.3,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {truncate(inc.title, MAX_CARD_TITLE)}
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span
          style={{
            fontFamily: "'DM Mono', monospace", fontSize: 9,
            color: "#9198b0", letterSpacing: "0.4px",
          }}
        >
          {ageLabel(inc.createdAt)}
        </span>
        <span
          className="inline-block px-1 py-0.5 rounded"
          style={{
            background: badge.bg, color: badge.color,
            fontFamily: "'DM Mono', monospace", fontSize: 8,
            fontWeight: 700, letterSpacing: "0.4px",
          }}
        >
          {t(`status.${badge.key}`)}
        </span>
      </div>
      <div
        className="mt-1.5 rounded-full"
        style={{
          height: 3,
          background: "rgba(255,255,255,0.06)",
          overflow: "hidden",
        }}
        data-testid="breeze-card-sla-bar"
      >
        <div
          style={{
            height: "100%",
            width: `${bar}%`,
            background: slaBarColor(sla),
            transition: "width 200ms ease-out",
          }}
        />
      </div>
    </button>
  );
};
